import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { NeomorphicCard, NeomorphicButton } from '@/components/shared/NeomorphicCard';
import { CustomIcon, CharacterAvatar } from '@/components/ui/CustomIcon';
import { cn } from '@/lib/utils';
import { getSupabaseIconUrl } from '@/utils/supabaseIcons';

export interface ChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  quickReplies?: string[];
}

export interface SetupState {
  hasProjectName: boolean;
  hasTimeline: boolean;
  hasBudget: boolean;
  hasTeamSize: boolean;
}

interface ChatInterfaceProps {
  messages: ChatMessage[];
  isTyping: boolean;
  onSendMessage: (message: string) => void;
  setupState: SetupState;
  className?: string;
}

/**
 * ChatInterface - AI Chat Component (Rebuilt for Quick Reply Support)
 *
 * Displays chat messages with Lyra AI and provides quick reply buttons
 * for easy interaction without typing.
 */
export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  isTyping,
  onSendMessage,
  setupState,
  className
}) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = () => {
    if (inputValue.trim() && !isTyping) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleQuickReply = (reply: string) => {
    if (!isTyping) {
      onSendMessage(reply);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  return (
    <NeomorphicCard className={cn("flex flex-col", className)}>
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 rounded-t-xl">
        <h2 className="font-semibold">Chat with Lyra</h2>
        <p className="text-sm text-blue-100 mt-1">Your AI Budget Assistant</p>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto" style={{ maxHeight: '400px' }}>
        {messages.map((message, index) => {
          const isLatest = index === messages.length - 1;

          return (
            <div key={message.id} className="mb-4">
              {/* Message Bubble */}
              <div className={`flex items-start gap-3 ${message.isUser ? 'flex-row-reverse' : ''}`}>
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {message.isUser ? (
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <CustomIcon type="user" className="w-5 h-5" alt="User" />
                    </div>
                  ) : (
                    <img
                      src={getSupabaseIconUrl('lyra-avatar.png')}
                      alt="Lyra AI"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  )}
                </div>

                {/* Message Content */}
                <div className={`flex flex-col ${message.isUser ? 'items-end' : 'items-start'} max-w-[75%]`}>
                  <div
                    className={cn(
                      'px-4 py-3 rounded-2xl',
                      message.isUser
                        ? 'bg-blue-600 text-white rounded-tr-sm'
                        : 'bg-gray-100 text-gray-900 rounded-tl-sm'
                    )}
                  >
                    <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                  </div>
                  <div className="text-xs text-gray-400 mt-1 px-2">
                    {formatTime(message.timestamp)}
                  </div>

                  {/* Quick Reply Buttons - ONLY on latest AI message */}
                  {!message.isUser && isLatest && !isTyping && message.quickReplies && message.quickReplies.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {message.quickReplies.map((reply, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleQuickReply(reply)}
                          className="px-4 py-2 bg-white border-2 border-blue-500 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-50 transition-all shadow-sm hover:shadow-md"
                        >
                          {reply}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex items-start gap-3 mb-4">
            <img
              src={getSupabaseIconUrl('lyra-avatar.png')}
              alt="Lyra AI"
              className="w-10 h-10 rounded-full object-cover"
            />
            <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Debug Panel */}
      <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200">
        <div className="text-xs text-gray-700 space-y-1">
          <div>📊 Total messages: {messages.length}</div>
          <div>⏳ Is typing: {isTyping ? 'Yes' : 'No'}</div>
          <div>💬 Latest has quick replies: {messages[messages.length - 1]?.quickReplies?.length || 0} options</div>
          {messages[messages.length - 1]?.quickReplies && (
            <div>🔍 Quick replies: {JSON.stringify(messages[messages.length - 1].quickReplies)}</div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your response..."
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim() || isTyping}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send
          </button>
        </div>
      </div>
    </NeomorphicCard>
  );
};